import { createSlice, PayloadAction } from '@reduxjs/toolkit';

// Interfaces
import { ICompanyProfile, ISpecialtySpecialisations } from '../../interfaces/company.interfaces';
import { ICountry, ILanguage } from '../../interfaces/core.interfaces';

export interface ICompanyState {
  company: ICompanyProfile | null;
  specialtySpecialisations: ISpecialtySpecialisations[];
  allLanguages: ILanguage[];
  allCountries: ICountry[];
  loading: boolean;
  error: string | null;
}

const initialState: ICompanyState = {
  company: null,
  specialtySpecialisations: [],
  allLanguages: [],
  allCountries: [],
  loading: true,
  error: null,
};

export const companySlice = createSlice({
  name: 'company',
  initialState,
  reducers: {
    companyRequest(state) {
      state.loading = true;
      state.error = null;
    },
    companyRequestSuccess: (state, { payload: company }: PayloadAction<ICompanyProfile>) => {
      state.company = company;
      state.loading = false;
      state.error = null;
    },
    companyRequestFailure: (state, { payload: error }: PayloadAction<string>) => {
      state.loading = false;
      state.error = error;
    },

    updateCompanyRequest(state) {
      state.loading = true;
      state.error = null;
    },
    updateCompanyRequestSuccess: (state, { payload: company }: PayloadAction<ICompanyProfile>) => {
      state.company = company;
      state.loading = false;
      state.error = null;
    },
    updateCompanyRequestFailure(state, { payload: error }: PayloadAction<string>) {
      state.loading = false;
      state.error = error;
    },
    changeStripeConnectedStatus(state, { payload: is_stripe_connected }: PayloadAction<boolean>) {
      state.company = state.company ? { ...state.company, is_stripe_connected } : null;
    },
    saveAllLanguages(state, { payload: allLanguages }: PayloadAction<ILanguage[]>) {
      state.allLanguages = allLanguages;
      state.loading = false;
      state.error = null;
    },
    saveAllCountries(state, { payload: allCountries }: PayloadAction<ICountry[]>) {
      state.allCountries = allCountries;
      state.loading = false;
      state.error = null;
    },
    saveSpecialtySpecialisations(state, { payload }: PayloadAction<ISpecialtySpecialisations[]>) {
      state.specialtySpecialisations = payload;
      state.loading = false;
      state.error = null;
    },
    addCompanySpecialtySpecialisation(state, { payload }: PayloadAction<ISpecialtySpecialisations>) {
      state.specialtySpecialisations = [payload, ...state.specialtySpecialisations];
      state.loading = false;
      state.error = null;
    },
    updateCompanySpecialtySpecialisation(state, { payload: speciality }: PayloadAction<ISpecialtySpecialisations>) {
      state.specialtySpecialisations = state.specialtySpecialisations.map(
        (item) => (item.speciality.id === speciality.speciality.id ? speciality : item)
      );
      state.loading = false;
      state.error = null;
    },
    deleteCompanySpecialtySpecialisation(state, { payload: id }: PayloadAction<number>) {
      state.specialtySpecialisations = state.specialtySpecialisations.filter(
        (item) => item.speciality.id !== id
      );
      state.loading = false;
      state.error = null;
    },
    updateCompanyBilling(state, { payload: { is_not_enough_minutes, is_not_enough_request } }:
      PayloadAction<{ is_not_enough_minutes: boolean, is_not_enough_request: boolean }>) {
      state.company = state.company ? { ...state.company, is_not_enough_minutes, is_not_enough_request } : null;
    },
    updateCompanyCounsellorManagement(state, { payload: has_active_counsellors }: PayloadAction<boolean>) {
      state.company = state.company ? { ...state.company, has_active_counsellors } : null;
    },
    updateCompanyCounsellorAttributes(state, { payload: has_counsellor_attributes }: PayloadAction<boolean>) {
      state.company = state.company ? { ...state.company, has_counsellor_attributes } : null;
    },
  }
});

export const CompanySliceActions = companySlice.actions;
